import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const blobCache = new Map();

/**
 * FASE 20 — image that supports authenticated /api/files URLs (fetched as blob)
 * and opens a fullscreen lightbox on click.
 */
export default function AuthImage({ src, alt = "", className = "", expandable = true }) {
  const isApiFile = typeof src === "string" && src.includes("/api/files/");
  const [url, setUrl] = useState(isApiFile ? blobCache.get(src) || null : src);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!src) {
      setUrl(null);
      return;
    }
    if (!isApiFile) {
      setUrl(src);
      return;
    }
    if (blobCache.has(src)) {
      setUrl(blobCache.get(src));
      return;
    }
    (async () => {
      try {
        const resp = await api.get(src.replace(/^\/api/, ""), { responseType: "blob" });
        const objUrl = URL.createObjectURL(resp.data);
        blobCache.set(src, objUrl);
        if (!cancelled) setUrl(objUrl);
      } catch (err) {
        console.error("auth image load failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src, isApiFile]);

  if (!src) return null;
  if (!url) {
    return (
      <div className={`flex items-center justify-center bg-stone-100 rounded-md ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <>
      <img
        src={url}
        alt={alt}
        className={`${className} ${expandable ? "cursor-zoom-in" : ""}`}
        onClick={expandable ? () => setOpen(true) : undefined}
        data-testid="auth-image"
      />
      {expandable && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-4xl p-2 bg-stone-950 border-stone-800" data-testid="image-lightbox">
            <DialogTitle className="sr-only">{alt || "Visualização da imagem"}</DialogTitle>
            <img src={url} alt={alt} className="w-full h-auto max-h-[80vh] object-contain rounded" />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
